import { Button } from "@material-ui/core";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import "./Refunds.css";
import { DataGrid, GridColDef } from "@material-ui/data-grid";
import UserService from "../../../services/user-service/UserService";
import { Modal } from "react-bootstrap";
import moment from "moment-timezone";
import { confirmAlert } from "react-confirm-alert";
import CustomNoRowsOverlay from "../../../components/NoRecord";
import SpinnerLoader from "../../../components/spinner-loader/SpinnerLoader.component";
import { Link } from "react-router-dom";
import Utility from "../../../utility/Utility";

const PAGE_SIZE = 10;

const formatDate = (date: any) =>
  date ? moment(date).format("MM/DD/YYYY hh:mm A") : "";

const formatMoney = (value: any) => `$${parseFloat(value ?? 0).toFixed(2)}`;

// Auto-generated texts that should never show in the Comments box.
const AUTO_COMMENTS = [/submitted via app/gi, /auto refund by system/gi];

const cleanComments = (comments: any) =>
  AUTO_COMMENTS.reduce(
    (text: string, pattern) => text.replace(pattern, ""),
    String(comments ?? "")
  ).trim();

function Refunds(props: any) {
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState<any>(0);
  const [requestStatus, setRequestStatus] = useState<string>("Pending");
  const [fullName, setFullName] = useState("");
  const [refundList, setRefundList] = useState<any[]>([]);
  const [callDetail, setCallDetail] = useState<any>({});
  const [requestDetail, setRequestDetail] = useState<any>({});
  const [showCallModal, setShowCallModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [hideReviewButtons, setHideReviewButtons] = useState(false);
  const [activeSection, setActiveSection] = useState<"calls" | "messages">("calls");

  // react-hook-form v6 (ref-based register)
  const { register, handleSubmit, errors } = useForm();

  const isClosed =
    requestDetail.requestStatus === "Rejected" ||
    requestDetail.requestStatus === "Approved";

  // Changes when the detail data arrives, so the modal's form fields remount
  // and pick up fresh defaultValues (defaultValue is only read on mount).
  const formKey = `${callDetail.callId ?? "loading"}-${requestDetail.requestStatus ?? ""}`;

  // ---------- Data loading ----------

  const fetchList = (pageNumber: number, status: string = requestStatus) => {
    const data = {
      pageSize: PAGE_SIZE,
      pageNumber,
      name: fullName,
      status,
    };
    const request =
      activeSection === "calls"
        ? UserService.GetAllRefundRequests(data)
        : UserService.GetAllRefundRequestsMsg(data);

    return request.then((res: any) => {
      setRowCount(res.data.item1);
      setRefundList(res.data.item2 ?? []);
    });
  };

  const search = () => {
    setRefundList([]);
    setPage(0);
    return fetchList(0);
  };

  useEffect(() => {
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchList(newPage);
  };

  const handleStatusFilter = (e: any) => {
    const status = e.target.value ?? requestStatus;
    setRequestStatus(status);
    setPage(0);
    setRefundList([]);
    fetchList(0, status);
  };

  // ---------- Modal handling ----------

  const closeModals = () => {
    setCallDetail({});
    setRequestDetail({});
    setShowCallModal(false);
    setShowMessageModal(false);
  };

  const review = (row: any) => {
    if (!row) return;
    setHideReviewButtons(row.isActive === false || row.isDeleted);
    setCallDetail({});
    setRequestDetail({});

    const request =
      activeSection === "calls"
        ? UserService.GetCallDetail(row.callId)
        : UserService.GetCallDetailMsg(row.callId);

    request.then((res: any) => {
      if (res.data) {
        setCallDetail(res.data.callDTO ?? {});
        setRequestDetail(res.data.refundRequestDetialDTO ?? {});
      }
    });

    if (activeSection === "calls") setShowCallModal(true);
    else setShowMessageModal(true);
  };

  const submit = (data: any, status: "Approved" | "Rejected") => {
    data.status = status;
    data.reason = callDetail.refundReason;
    data.additionalDetail = callDetail.additionalDetail;
    closeModals();

    confirmAlert({
      title: "Confirm",
      message:
        status === "Approved"
          ? "Are you sure to Approve the Refund request? You will not be able to change again!"
          : "Are you sure to Reject the Refund request? You will not be able to change again!",
      buttons: [
        {
          label: "Confirm",
          onClick: () => {
            const update =
              activeSection === "calls"
                ? UserService.UpdateRefundRequest(data)
                : UserService.UpdateRefundRequestMsg(data);

            update.then(() => {
              Utility.notifyUpdated();
              setRefundList([]);
              fetchList(page);
            });
          },
        },
        { label: "Cancel", onClick: () => {} },
      ],
    });
  };

  // ---------- Grid columns ----------

  const columns: GridColDef[] = [
    { field: "id", hide: true },
    {
      field: "issueNumber",
      headerName: "Issue Number",
      width: 200,
      align: "left",
      headerAlign: "center",
    },
    {
      field: "callWithFirstName",
      headerName: "User",
      flex: 0.4,
      align: "left",
      headerAlign: "center",
      renderCell: (params: any) =>
        params.row.isActive
          ? params.row.isDeleted
            ? "**" + params.row.callWithFirstName
            : params.row.callWithFirstName
          : "*" + params.row.callWithFirstName,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 0.4,
      align: "left",
      headerAlign: "center",
    },
    {
      field: "refundCreatedDate",
      headerName: "Date",
      width: 190,
      valueFormatter: (params) => formatDate(params.row.refundCreatedDate),
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      align: "left",
      headerAlign: "center",
    },
    {
      field: "col4",
      headerName: " ",
      sortable: false,
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Button
          style={{ background: "#bfa056", color: "#fff" }}
          variant="contained"
          className="MuiButton-textSizeSmall"
          onClick={() => review(params.row)}
        >
          {params.row.status === "Approved" || params.row.status === "Rejected"
            ? "View"
            : "Review"}
        </Button>
      ),
    },
  ];

  // ---------- Shared modal pieces ----------

  const renderRefundReasonAndDetails = () => (
    <>
      <div className="group-field">
        <label htmlFor="reason" className="m-1 t-primary">Refund Reason</label>
        <input
          className="form-control form-control-sm"
          type="text"
          disabled
          defaultValue={callDetail.refundReason}
          style={{ width: "70%", marginLeft: "18px", marginBottom: "7px" }}
        />
      </div>
      <div className="group-field">
        <input name="callLogId" ref={register} defaultValue={callDetail.callId} hidden />
        <label className="t-primary mr-2">Additional Details</label>
        <textarea
          disabled
          ref={register}
          style={{ width: "70%", resize: "none", height: "120px" }}
          name="additionalDetail"
          className="form-control"
          defaultValue={callDetail.additionalDetail}
        />
      </div>
    </>
  );

  const renderRequestDetails = () => (
    <div style={{ width: "47%" }}>
      <h5>Request Details</h5>
      <div className="row">
        <label className="t-primary">Requested By : </label>
        <p className="ml-1 label-txt font-weight-bold">{requestDetail.requestedBy}</p>
      </div>
      <div className="row">
        <label className="t-primary">Request Date : </label>
        <p className="ml-1 label-txt font-weight-bold">{formatDate(requestDetail.requestedDate)}</p>
      </div>
      <div className="row">
        <label className="t-primary">Request Status : </label>
        <p className="ml-1 label-txt font-weight-bold">{requestDetail.requestStatus}</p>
      </div>
      <div className="row">
        <label className="t-primary">Status Date : </label>
        <p className="ml-1 label-txt font-weight-bold">{formatDate(requestDetail.statusDate)}</p>
      </div>
      <div className="row txt-center-row">
        <div style={{ display: "flex", justifyContent: "center" }}>
          <label className="ml-1 t-primary font-weight-bold">Refund Amount: </label>
          <p className="ml-1 label-txt font-weight-bold">{formatMoney(requestDetail?.refundAmount)}</p>
        </div>
      </div>

      <div className="group-field">
        <label className="t-primary">
          Comments <span className="font-weight-bold">{isClosed ? "" : "*"}</span>
        </label>
        <textarea
          disabled={isClosed}
          style={{ width: "70%", resize: "none", height: "120px" }}
          name="comments"
          className="form-control"
          placeholder="Comments"
          defaultValue={
            isClosed
              ? requestDetail.comments ?? ""
              : cleanComments(requestDetail.comments)
          }
          ref={register({ required: !isClosed })}
        />
        {!isClosed && errors?.comments && (
          <span className="text-danger mand-field">Comments are required.</span>
        )}
      </div>
    </div>
  );

  const renderFooter = () => (
    <div className="float-right b-btns">
      <Button variant="contained" onClick={closeModals}>
        Cancel
      </Button>
      {!isClosed && !hideReviewButtons && (
        <>
          <Button
            className="ml-2 primary"
            variant="contained"
            onClick={handleSubmit((data) => submit(data, "Rejected"))}
          >
            Reject
          </Button>
          <Button
            className="ml-2 primary"
            variant="contained"
            onClick={handleSubmit((data) => submit(data, "Approved"))}
          >
            Approve
          </Button>
        </>
      )}
    </div>
  );

  const statusOptions = ["All", "Pending", "Approved", "Rejected"];

  // ---------- Render ----------

  return (
    <div>
      <SpinnerLoader
        isGeneral={true}
        tasksList={[
          "GetAllRefundRequests",
          "GetAllRefundRequestsMsg",
          "GetCallDetail",
          "GetCallDetailMsg",
          "UpdateRefundRequest",
          "UpdateRefundRequestMsg",
        ]}
        inInternal={false}
      />

      <h3 className="mb-3">Refunds</h3>

      <div>
        <form className="form-inline signup-up-sec mb-2" onSubmit={(e) => { e.preventDefault(); search(); }}>
          <div className="lft-sec">
            {statusOptions.map((status) => (
              <div key={status} className="custom-control custom-radio custom-control-inline">
                <input
                  type="radio"
                  value={status}
                  id={status.toLowerCase()}
                  name="status"
                  className="custom-control-input"
                  checked={requestStatus === status}
                  onChange={handleStatusFilter}
                />
                <label className="custom-control-label" htmlFor={status.toLowerCase()}>
                  {status}
                </label>
              </div>
            ))}
          </div>
          <div className="right-sec">
            <input
              className="form-control form-control-sm"
              type="text"
              name="fullName"
              style={{ backgroundColor: "#fff" }}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Name"
            />
            <Button
              type="button"
              variant="contained"
              onClick={search}
              className="btn search-btn float-right ml-3 primary"
            >
              <i className="fa fa-search"></i> Search
            </Button>
          </div>
        </form>
      </div>

      <div style={{ paddingBottom: "40px", width: "100%" }}>
        <div className="tab-wrapper">
          <div className="tab-header">
            <button
              type="button"
              className={`tab-button ${activeSection === "calls" ? "active" : ""}`}
              onClick={() => setActiveSection("calls")}
            >
              Calls
            </button>
            <button
              type="button"
              className={`tab-button ${activeSection === "messages" ? "active" : ""}`}
              onClick={() => setActiveSection("messages")}
            >
              Messages
            </button>
          </div>
          <Button type="button" variant="contained" className="btn search-btn float-right ml-3 primary">
            <Link className="lnk" to="newrefundrequest">
              New Request
            </Link>
          </Button>
        </div>

        <div className="tab-content">
          <div className="section">
            <DataGrid
              key={activeSection}
              components={{ NoRowsOverlay: CustomNoRowsOverlay }}
              columns={columns}
              rows={refundList}
              getRowId={(row) => row.callId}
              page={page}
              pageSize={PAGE_SIZE}
              autoHeight={true}
              rowCount={rowCount}
              paginationMode="server"
              onPageChange={(newPage: any) => handlePageChange(newPage)}
              disableColumnMenu={true}
              disableSelectionOnClick={true}
            />
          </div>
        </div>

        <div style={{ marginTop: "10px" }}>
          <p style={{ fontSize: "15px" }}><strong>*</strong> Deactivated User</p>
          <p style={{ marginTop: "-15px", fontSize: "15px" }}><strong>**</strong> Deleted User</p>
        </div>
      </div>

      {/* ---------- Call refund modal ---------- */}
      <Modal show={showCallModal} onHide={closeModals} size="xl">
        <Modal.Body>
          <form key={formKey} autoComplete="off" noValidate>
            <div className="modal-header">
              <div className="modal-title h4">Refund Request</div>
            </div>
            <div className="modal-body-bx request-mod">
              <div style={{ width: "47%" }}>
                <h5>Call Details</h5>
                <div className="row">
                  <div className="row w50">
                    <label className="t-primary">Call From: </label>
                    <p className="ml-1 label-txt font-weight-bold">{callDetail.callFromFirstName}</p>
                  </div>
                  <div className="row w50">
                    <label className="t-primary row">Call To: </label>
                    <p className="ml-4 label-txt font-weight-bold">{callDetail.callToFirstName}</p>
                  </div>
                </div>
                <div className="row">
                  <div className="row w50">
                    <label className="t-primary">Request Duration: </label>
                    <p className="ml-1 label-txt font-weight-bold">{callDetail.requestedDuration} mins</p>
                  </div>
                  <div className="row w50">
                    <label className="t-primary">Actual Duration: </label>
                    <p className="ml-4 label-txt font-weight-bold">{callDetail.actualDuration} mins</p>
                  </div>
                </div>
                <div className="row txt-center-row">
                  <div className="row d-flex justify-content-center">
                    <label className="ml-1 label-txt t-primary font-weight-bold">Call Charges: </label>
                    <p className="ml-1 label-txt font-weight-bold">{formatMoney(callDetail?.callCharges)}</p>
                  </div>
                  <div className="row d-flex justify-content-center">
                    <label className="row ml-4 t-primary">Call End time: </label>
                    <p className="ml-4 label-txt font-weight-bold">{formatDate(callDetail.callEndedAt)}</p>
                  </div>
                </div>

                {renderRefundReasonAndDetails()}
              </div>

              <div className="vl vl2"></div>
              {renderRequestDetails()}
            </div>
            <hr />
            {renderFooter()}
          </form>
        </Modal.Body>
      </Modal>

      {/* ---------- Message refund modal ---------- */}
      <Modal show={showMessageModal} onHide={closeModals} size="xl">
        <Modal.Body>
          <form key={formKey} autoComplete="off" noValidate>
            <div className="modal-header">
              <div className="modal-title h4">Refund Request</div>
            </div>
            <div className="modal-body-bx request-mod">
              <div style={{ width: "50%" }}>
                <h5>Message Details</h5>
                <div className="row">
                  <div className="row w50">
                    <label className="t-primary">Message From: </label>
                    <p className="ml-1 label-txt font-weight-bold">{callDetail.callFromFirstName}</p>
                  </div>
                  <div className="row w50">
                    <label className="t-primary row">Message To: </label>
                    <p className="ml-4 label-txt font-weight-bold">{callDetail.callToFirstName}</p>
                  </div>
                </div>
                <div className="row">
                  <div className="row w50">
                    <label className="t-primary">Message Date: </label>
                    <p className="ml-4 label-txt font-weight-bold">{formatDate(callDetail.messageDate)}</p>
                  </div>
                  <div className="row w10">
                    <label className="t-primary">Message Type: </label>
                    <p className="ml-4 label-txt font-weight-bold">{callDetail.messageType}</p>
                  </div>
                </div>
                <div className="row txt-center-row">
                  <div className="row d-flex justify-content-center">
                    <label className="ml-1 label-txt t-primary font-weight-bold">Message Charges: </label>
                    <p className="ml-1 label-txt font-weight-bold">{formatMoney(callDetail?.callCharges)}</p>
                  </div>
                  <div className="row d-flex justify-content-center">
                    <label className="row ml-4 t-primary">Message Rate: </label>
                    <p className="ml-4 label-txt font-weight-bold">{formatMoney(callDetail?.callCharges)}</p>
                  </div>
                </div>

                <div className="group-field">
                  <label className="m-1 t-primary">Original Message</label>
                  {callDetail?.messageType?.toLowerCase() === "audio" ? (
                    <audio controls style={{ width: "70%", marginLeft: "18px", marginBottom: "15px" }}>
                      <source src={callDetail.messageContent} type="audio/mpeg" />
                      Your browser does not support the audio element.
                    </audio>
                  ) : (
                    <textarea
                      className="form-control form-control-sm"
                      disabled
                      defaultValue={callDetail.messageContent}
                      style={{ width: "70%", marginLeft: "18px", marginBottom: "7px", resize: "none" }}
                    />
                  )}
                </div>

                <div className="group-field">
                  <label className="m-1 t-primary">Reply Message</label>
                  {callDetail?.messageResponded?.includes("https://") ? (
                    <audio controls style={{ width: "70%", marginLeft: "18px" }}>
                      <source src={callDetail.messageResponded} type="audio/mpeg" />
                      Your browser does not support the audio element.
                    </audio>
                  ) : (
                    <textarea
                      className="form-control form-control-sm"
                      disabled
                      defaultValue={callDetail.messageResponded}
                      style={{ width: "70%", marginLeft: "18px", marginBottom: "7px", resize: "none" }}
                    />
                  )}
                </div>

                {renderRefundReasonAndDetails()}
              </div>

              <div className="vl vl2"></div>
              {renderRequestDetails()}
            </div>
            <hr />
            {renderFooter()}
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default Refunds;